/* tslint:disable */
/* eslint-disable */
/* Mocked in-memory implementation of ItemAndComponentClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import {
  Item,
  PagedData_1OfRecipe,
  UpsertItem,
  UpsertItemComponent,
  FileResponse,
  FileParameter,
} from '../../types';

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Returns the recipe prefix for a given store number to scope recipes by franchise */
function getRecipePrefixForStore(storeNumber?: string): string | null {
  if (!storeNumber) return null;
  if (storeNumber.startsWith('VAP')) return 'VAP-';
  if (storeNumber.startsWith('FG')) return 'FG-';
  return 'RECIPE';
}

/* ---- Mock Data ---- */

const mockRecipes = [
  {
    id: 1,
    itemNumber: 'RECIPE001',
    description: 'Latte',
    components: [
      { itemNumber: 'MILK001', description: 'Whole Milk', quantity: 0.25, uom: 'Litre' },
      { itemNumber: 'COFFEE001', description: 'Espresso Shot', quantity: 2, uom: 'Each' },
    ],
  },
  {
    id: 2,
    itemNumber: 'RECIPE002',
    description: 'Cappuccino',
    components: [
      { itemNumber: 'MILK001', description: 'Whole Milk', quantity: 0.15, uom: 'Litre' },
      { itemNumber: 'COFFEE001', description: 'Espresso Shot', quantity: 1, uom: 'Each' },
    ],
  },
  // ---- Vapiano Recipes ----
  {
    id: 101,
    itemNumber: 'VAP-CARBONARA-SALE',
    description: 'Spaghetti Carbonara',
    components: [
      { itemNumber: 'VAP-FLOUR-001', description: '00 Flour (Caputo)', quantity: 0.1, uom: 'kg' },
      { itemNumber: 'VAP-EGGS-001', description: 'Free Range Eggs', quantity: 2, uom: 'Each' },
      { itemNumber: 'VAP-GUANCIALE-001', description: 'Guanciale', quantity: 0.08, uom: 'kg' },
      { itemNumber: 'VAP-PECORINO-001', description: 'Pecorino Romano DOP', quantity: 0.04, uom: 'kg' },
      { itemNumber: 'VAP-OLIVE-001', description: 'Extra Virgin Olive Oil', quantity: 0.01, uom: 'Litre' },
    ],
  },
  {
    id: 102,
    itemNumber: 'VAP-MARGHERITA-SALE',
    description: 'Pizza Margherita',
    components: [
      { itemNumber: 'VAP-FLOUR-001', description: '00 Flour (Caputo)', quantity: 0.25, uom: 'kg' },
      { itemNumber: 'VAP-MOZZ-001', description: 'Fior di Latte Mozzarella', quantity: 0.125, uom: 'kg' },
      { itemNumber: 'VAP-TOMATO-001', description: 'San Marzano Tomatoes', quantity: 0.1, uom: 'kg' },
      { itemNumber: 'VAP-BASIL-001', description: 'Fresh Basil', quantity: 3, uom: 'Leaves' },
      { itemNumber: 'VAP-OLIVE-001', description: 'Extra Virgin Olive Oil', quantity: 0.015, uom: 'Litre' },
    ],
  },
  {
    id: 103,
    itemNumber: 'VAP-SPRITZ-SALE',
    description: 'Aperol Spritz',
    components: [
      { itemNumber: 'VAP-APEROL-001', description: 'Aperol', quantity: 0.06, uom: 'Litre' },
      { itemNumber: 'VAP-PROSECCO-001', description: 'Prosecco DOC', quantity: 0.09, uom: 'Litre' },
    ],
  },
  {
    id: 104,
    itemNumber: 'VAP-GAMBERONI-SALE',
    description: 'Spaghetti Gamberoni',
    components: [
      { itemNumber: 'VAP-FLOUR-001', description: '00 Flour (Caputo)', quantity: 0.1, uom: 'kg' },
      { itemNumber: 'VAP-EGGS-001', description: 'Free Range Eggs', quantity: 2, uom: 'Each' },
      { itemNumber: 'VAP-PRAWNS-001', description: 'King Prawns', quantity: 0.12, uom: 'kg' },
      { itemNumber: 'VAP-TOMATO-001', description: 'San Marzano Tomatoes', quantity: 0.08, uom: 'kg' },
      { itemNumber: 'VAP-OLIVE-001', description: 'Extra Virgin Olive Oil', quantity: 0.02, uom: 'Litre' },
    ],
  },
  // ---- Five Guys Recipes ----
  {
    id: 201,
    itemNumber: 'FG-CHEESEBURGER-SALE',
    description: 'Cheeseburger',
    components: [
      { itemNumber: 'FG-BEEF-001', description: 'Fresh Ground Beef Patty', quantity: 2, uom: 'Each' },
      { itemNumber: 'FG-BUNS-001', description: 'Sesame Seed Bun', quantity: 1, uom: 'Each' },
      { itemNumber: 'FG-CHEESE-001', description: 'American Cheese Slices', quantity: 2, uom: 'Each' },
      { itemNumber: 'FG-LETTUCE-001', description: 'Iceberg Lettuce (hand-torn)', quantity: 0.03, uom: 'kg' },
      { itemNumber: 'FG-TOMATO-001', description: 'Fresh Tomato Slices', quantity: 0.04, uom: 'kg' },
      { itemNumber: 'FG-ONION-001', description: 'White Onion (grilled)', quantity: 0.03, uom: 'kg' },
    ],
  },
  {
    id: 202,
    itemNumber: 'FG-BACONBURGER-SALE',
    description: 'Bacon Cheeseburger',
    components: [
      { itemNumber: 'FG-BEEF-001', description: 'Fresh Ground Beef Patty', quantity: 2, uom: 'Each' },
      { itemNumber: 'FG-BUNS-001', description: 'Sesame Seed Bun', quantity: 1, uom: 'Each' },
      { itemNumber: 'FG-CHEESE-001', description: 'American Cheese Slices', quantity: 2, uom: 'Each' },
      { itemNumber: 'FG-BACON-001', description: 'Smoked Back Bacon', quantity: 0.04, uom: 'kg' },
      { itemNumber: 'FG-LETTUCE-001', description: 'Iceberg Lettuce (hand-torn)', quantity: 0.03, uom: 'kg' },
      { itemNumber: 'FG-TOMATO-001', description: 'Fresh Tomato Slices', quantity: 0.04, uom: 'kg' },
    ],
  },
  {
    id: 203,
    itemNumber: 'FG-FRIES-REG-SALE',
    description: 'Five Guys Fries (Regular)',
    components: [
      { itemNumber: 'FG-POTATO-001', description: 'Fresh Potatoes (Maris Piper)', quantity: 0.35, uom: 'kg' },
      { itemNumber: 'FG-PEANUT-OIL-001', description: '100% Refined Peanut Oil', quantity: 0.15, uom: 'Litre' },
    ],
  },
  {
    id: 204,
    itemNumber: 'FG-CAJUN-FRIES-SALE',
    description: 'Cajun Fries (Regular)',
    components: [
      { itemNumber: 'FG-POTATO-001', description: 'Fresh Potatoes (Maris Piper)', quantity: 0.35, uom: 'kg' },
      { itemNumber: 'FG-PEANUT-OIL-001', description: '100% Refined Peanut Oil', quantity: 0.15, uom: 'Litre' },
      { itemNumber: 'FG-CAJUN-001', description: 'Cajun Seasoning', quantity: 0.005, uom: 'kg' },
    ],
  },
  {
    id: 205,
    itemNumber: 'FG-HOTDOG-SALE',
    description: 'All-Beef Hot Dog',
    components: [
      { itemNumber: 'FG-HOTDOG-001', description: 'All-Beef Frank', quantity: 1, uom: 'Each' },
      { itemNumber: 'FG-BUNS-001', description: 'Hot Dog Bun', quantity: 1, uom: 'Each' },
    ],
  },
  {
    id: 206,
    itemNumber: 'FG-SHAKE-SALE',
    description: 'Milkshake (Regular)',
    components: [
      { itemNumber: 'FG-SHAKE-BASE-001', description: 'Vanilla Milkshake Base', quantity: 0.35, uom: 'Litre' },
    ],
  },
];

/* ---- Mocked Client Implementation ---- */

import { IItemAndComponentClient } from '../../interfaces';
export class ItemAndComponentClientMocked implements IItemAndComponentClient {
  constructor(baseUrl?: string) {}

  async itemandcomponentPOST(body?: UpsertItem, cancelToken?: CancelToken): Promise<Item> {
    await delay();
    const newItem: Item = {
      id: mockRecipes.length + 1,
      itemNumber: body?.itemNumber || `RECIPE00${mockRecipes.length + 1}`,
      description: body?.description || 'New Recipe',
      isBom: true,
      isMadeInStore: true,
    };
    return newItem;
  }

  async itemandcomponentGET(
    search?: string,
    storeNumber?: string,
    skip?: number,
    take?: number,
    cancelToken?: CancelToken
  ): Promise<PagedData_1OfRecipe> {
    await delay();
    const prefix = getRecipePrefixForStore(storeNumber);
    let filtered = prefix
      ? mockRecipes.filter((r) => r.itemNumber.startsWith(prefix))
      : [...mockRecipes];
    if (search) {
      filtered = filtered.filter(
        (r) =>
          r.itemNumber.toLowerCase().includes(search.toLowerCase()) ||
          r.description.toLowerCase().includes(search.toLowerCase())
      );
    }
    const start = skip || 0;
    const end = start + (take || 10);
    return {
      data: filtered.slice(start, end),
      totalCount: filtered.length,
      skip: start,
      take: end - start,
    } as PagedData_1OfRecipe;
  }

  async itemandcomponentDELETE(itemId: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const index = mockRecipes.findIndex((r) => r.id === itemId);
    if (index > -1) {
      mockRecipes.splice(index, 1);
    }
  }

  async itemandcomponentGET2(itemId: number, cancelToken?: CancelToken): Promise<Item> {
    await delay();
    const recipe = mockRecipes.find((r) => r.id === itemId);
    return {
      id: recipe?.id || itemId,
      itemNumber: recipe?.itemNumber || '',
      description: recipe?.description || '',
      isBom: true,
      isMadeInStore: true,
      components: recipe?.components || [],
    } as any;
  }

  async componentPOST(body?: UpsertItemComponent, cancelToken?: CancelToken): Promise<string | void> {
    await delay();
    return 'Component added successfully';
  }

  async componentDELETE(itemId: number, componentItemId: number, updateForFranchise: boolean, cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async export2(storeNumber: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return {
      data: new Blob(['mock data'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      fileName: 'recipes_export.xlsx',
    } as FileResponse;
  }

  async exportBulk(storeNumber: string, cancelToken?: CancelToken): Promise<FileResponse> {
    await delay();
    return {
      data: new Blob(['mock bulk data'], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      fileName: 'recipes_bulk_export.xlsx',
    } as FileResponse;
  }

  async uploadBulk(storeNumber: string, file: File, cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  // ====== Auto-generated stub methods ======









}
